import React, { useState, useEffect } from 'react'
import { AlertTriangle, Check, Lock, Pencil, Plus, Settings, ShieldCheck, Trash2, User as UserIcon, X } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { permissionsService } from '@/services/permissionsService'

import { getErrorMessage } from '../utils/error.utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Textarea } from '@/components/ui/textarea'
import { FormField } from '@/components/common/FormSection'
import { hasRole, roleLabel } from '@/lib/roles'
import { cn } from '@/utils/cn'

interface Permission {
  id: string
  name: string
  description: string
  resource: string
  action: string
}

interface Role {
  id: string
  name: string
  description: string
  permissions: string[]
  userCount: number
  isSystem: boolean
}

interface PermissionGroup {
  resource: string
  permissions: Permission[]
}

interface RolePermissionsManagerProps {
  className?: string
}

export function RolePermissionsManager({ className }: RolePermissionsManagerProps) {
  const [roles, setRoles] = useState<Role[]>([])
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [selectedRole, setSelectedRole] = useState<Role | null>(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [loading, setLoading] = useState(true)
  const { user } = useAuth()

  // Check if current user has permission to access this component
  const canManagePermissions = hasRole(user?.role, ['SUPER_ADMIN'])
  const canViewPermissions = hasRole(user?.role, ['SUPER_ADMIN', 'ADMIN'])

  // Load permissions and roles from API — must be declared before any early return
  useEffect(() => {
    if (canViewPermissions) {
      loadPermissionsAndRoles()
    }
    // loadPermissionsAndRoles depende apenas de serviços estáveis (módulos importados)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewPermissions])

  // Show access denied if user doesn't have permission
  if (!canViewPermissions) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
          <AlertTriangle className="size-10 text-destructive" aria-hidden="true" />
          <h2 className="text-lg font-semibold">Acesso Negado</h2>
          <p className="max-w-md text-sm text-muted-foreground">
            Você não tem permissão para acessar a configuração de permissões.
            Esta funcionalidade é restrita a administradores.
          </p>
        </CardContent>
      </Card>
    )
  }

  const loadPermissionsAndRoles = async () => {
    try {
      setLoading(true)
      const [apiPermissions, apiRoles] = await Promise.all([
        permissionsService.getAllPermissions(),
        permissionsService.getAllRoles()
      ])

      // Transform API permissions to component format
      const transformedPermissions: Permission[] = apiPermissions.map(p => ({
        id: p.id,
        name: getPermissionName(p.resource, p.action),
        description: p.description || '',
        resource: p.resource,
        action: p.action
      }))

      // Transform API roles to component format
      const transformedRoles: Role[] = apiRoles.map(r => ({
        id: r.id,
        name: getRoleName(r.name),
        description: r.description || '',
        permissions: r.permissions,
        userCount: r.userCount,
        isSystem: r.isSystem
      }))

      setPermissions(transformedPermissions)
      setRoles(transformedRoles)
      setLoading(false)
    } catch (error) {
      console.error('Error loading permissions and roles:', error)
      setLoading(false)
      // Fall back to mock data if API fails
      console.warn('Falling back to mock data')
      loadMockData()
    }
  }

  const getPermissionName = (resource: string, action: string): string => {
    const names: Record<string, Record<string, string>> = {
      users: { create: 'Criar Usuários', read: 'Ver Usuários', update: 'Editar Usuários', delete: 'Excluir Usuários' },
      clients: { create: 'Criar Clientes', read: 'Ver Clientes', update: 'Editar Clientes', delete: 'Excluir Clientes' },
      cases: { create: 'Criar Casos', read: 'Ver Casos', update: 'Editar Casos', delete: 'Excluir Casos' },
      finance: { create: 'Criar Transações', read: 'Ver Finanças', update: 'Editar Transações', delete: 'Excluir Transações' },
      reports: { generate: 'Gerar Relatórios', export: 'Exportar Relatórios' },
      system: { admin: 'Administração' },
      audit: { read: 'Ver Auditoria' }
    }
    return names[resource]?.[action] || `${resource}:${action}`
  }

  const getRoleName = (name: string): string => {
    // Papéis antigos aparecem com o nome antigo para não confundir com os novos.
    const legacy: Record<string, string> = {
      SUPER_ADMIN: 'Super Administrador (antigo)',
      ADMIN: 'Administrador (antigo)',
      LAWYER: 'Advogado (antigo)',
      ASSISTANT: 'Assistente (antigo)',
      USER: 'Usuário (antigo)'
    }
    return legacy[name] || roleLabel(name)
  }

  const loadMockData = () => {
    const mockPermissions: Permission[] = [
      // Gestão de Usuários
      { id: 'users:create', name: 'Criar Usuários', description: 'Permite criar novos usuários no sistema', resource: 'users', action: 'create' },
      { id: 'users:read', name: 'Ver Usuários', description: 'Permite visualizar lista de usuários', resource: 'users', action: 'read' },
      { id: 'users:update', name: 'Editar Usuários', description: 'Permite editar informações de usuários', resource: 'users', action: 'update' },
      { id: 'users:delete', name: 'Excluir Usuários', description: 'Permite excluir usuários do sistema', resource: 'users', action: 'delete' },
      
      // Gestão de Clientes
      { id: 'clients:create', name: 'Criar Clientes', description: 'Permite cadastrar novos clientes', resource: 'clients', action: 'create' },
      { id: 'clients:read', name: 'Ver Clientes', description: 'Permite visualizar informações dos clientes', resource: 'clients', action: 'read' },
      { id: 'clients:update', name: 'Editar Clientes', description: 'Permite editar dados dos clientes', resource: 'clients', action: 'update' },
      { id: 'clients:delete', name: 'Excluir Clientes', description: 'Permite excluir clientes do sistema', resource: 'clients', action: 'delete' },
      
      // Gestão de Casos
      { id: 'cases:create', name: 'Criar Casos', description: 'Permite criar novos casos jurídicos', resource: 'cases', action: 'create' },
      { id: 'cases:read', name: 'Ver Casos', description: 'Permite visualizar casos jurídicos', resource: 'cases', action: 'read' },
      { id: 'cases:update', name: 'Editar Casos', description: 'Permite editar casos existentes', resource: 'cases', action: 'update' },
      { id: 'cases:delete', name: 'Excluir Casos', description: 'Permite excluir casos do sistema', resource: 'cases', action: 'delete' },
      
      // Gestão Financeira
      { id: 'finance:create', name: 'Criar Transações', description: 'Permite criar movimentações financeiras', resource: 'finance', action: 'create' },
      { id: 'finance:read', name: 'Ver Finanças', description: 'Permite visualizar dados financeiros', resource: 'finance', action: 'read' },
      { id: 'finance:update', name: 'Editar Transações', description: 'Permite editar movimentações financeiras', resource: 'finance', action: 'update' },
      { id: 'finance:delete', name: 'Excluir Transações', description: 'Permite excluir movimentações financeiras', resource: 'finance', action: 'delete' },
      
      // Relatórios
      { id: 'reports:generate', name: 'Gerar Relatórios', description: 'Permite gerar relatórios do sistema', resource: 'reports', action: 'generate' },
      { id: 'reports:export', name: 'Exportar Relatórios', description: 'Permite exportar relatórios em diferentes formatos', resource: 'reports', action: 'export' },
      
      // Sistema
      { id: 'system:admin', name: 'Administração', description: 'Acesso completo às configurações do sistema', resource: 'system', action: 'admin' },
      { id: 'audit:read', name: 'Ver Auditoria', description: 'Permite visualizar logs de auditoria', resource: 'audit', action: 'read' }
    ]

    const mockRoles: Role[] = [
      {
        id: 'super-admin',
        name: 'Super Administrador',
        description: 'Acesso total ao sistema, incluindo configurações avançadas',
        permissions: mockPermissions.map(p => p.id),
        userCount: 1,
        isSystem: true
      },
      {
        id: 'admin',
        name: 'Administrador',
        description: 'Acesso administrativo geral, exceto configurações de sistema',
        permissions: [
          'users:create', 'users:read', 'users:update', 'users:delete',
          'clients:create', 'clients:read', 'clients:update', 'clients:delete',
          'cases:create', 'cases:read', 'cases:update', 'cases:delete',
          'finance:create', 'finance:read', 'finance:update', 'finance:delete',
          'reports:generate', 'reports:export'
        ],
        userCount: 2,
        isSystem: true
      },
      {
        id: 'lawyer',
        name: 'Advogado',
        description: 'Acesso completo a casos, clientes e financeiro',
        permissions: [
          'clients:create', 'clients:read', 'clients:update',
          'cases:create', 'cases:read', 'cases:update', 'cases:delete',
          'finance:create', 'finance:read', 'finance:update',
          'reports:generate'
        ],
        userCount: 1,
        isSystem: true
      },
      {
        id: 'assistant',
        name: 'Assistente',
        description: 'Acesso limitado para suporte aos advogados',
        permissions: [
          'clients:read', 'clients:update',
          'cases:read', 'cases:update',
          'finance:read'
        ],
        userCount: 0,
        isSystem: true
      },
      {
        id: 'client',
        name: 'Cliente',
        description: 'Acesso apenas aos próprios casos',
        permissions: ['cases:read'],
        userCount: 0,
        isSystem: true
      },
      {
        id: 'user',
        name: 'Usuário',
        description: 'Acesso básico somente leitura',
        permissions: ['clients:read', 'cases:read'],
        userCount: 0,
        isSystem: true
      }
    ]

    setTimeout(() => {
      setPermissions(mockPermissions)
      setRoles(mockRoles)
      setLoading(false)
    }, 500)
  }

  const permissionGroups: PermissionGroup[] = [
    {
      resource: 'users',
      permissions: permissions.filter(p => p.resource === 'users')
    },
    {
      resource: 'clients', 
      permissions: permissions.filter(p => p.resource === 'clients')
    },
    {
      resource: 'cases',
      permissions: permissions.filter(p => p.resource === 'cases')
    },
    {
      resource: 'finance',
      permissions: permissions.filter(p => p.resource === 'finance')
    },
    {
      resource: 'reports',
      permissions: permissions.filter(p => p.resource === 'reports')
    },
    {
      resource: 'system',
      permissions: permissions.filter(p => p.resource === 'system' || p.resource === 'audit')
    }
  ]

  const resourceLabels = {
    users: 'Usuários',
    clients: 'Clientes', 
    cases: 'Casos',
    finance: 'Financeiro',
    reports: 'Relatórios',
    system: 'Sistema & Auditoria'
  }

  const handlePermissionToggle = async (roleId: string, permissionId: string) => {
    const role = roles.find(r => r.id === roleId)
    if (!role || role.isSystem) return

    const hasPermission = role.permissions.includes(permissionId)
    const newPermissions = hasPermission
      ? role.permissions.filter(p => p !== permissionId)
      : [...role.permissions, permissionId]

    try {
      // Optimistic update
      setRoles(prev => prev.map(r =>
        r.id === roleId ? { ...r, permissions: newPermissions } : r
      ))

      // Save to API
      await permissionsService.updateRolePermissions(roleId, {
        permissionIds: newPermissions
      })
    } catch (error) {
      console.error('Error updating role permissions:', error)
      // Revert on error using functional update to avoid stale closure
      setRoles(prev => prev.map(r =>
        r.id === roleId ? { ...r, permissions: role.permissions } : r
      ))
      alert('Erro ao atualizar permissões. Tente novamente.')
    }
  }

  const handleCreateRole = () => {
    const newRole: Role = {
      id: 'custom-' + Date.now(),
      name: 'Novo Perfil',
      description: 'Perfil personalizado',
      permissions: [],
      userCount: 0,
      isSystem: false
    }
    setRoles([...roles, newRole])
    setSelectedRole(newRole)
    setShowEditModal(true)
  }

  const handleDeleteRole = async (roleId: string) => {
    const role = roles.find(r => r.id === roleId)
    if (!role) return

    if (role.isSystem) {
      alert('Perfis do sistema não podem ser excluídos.')
      return
    }

    if (!window.confirm(`Tem certeza que deseja excluir o perfil "${role.name}"?`)) {
      return
    }

    try {
      // Optimistic update
      setRoles(prev => prev.filter(r => r.id !== roleId))

      // Delete from API
      await permissionsService.deleteRole(roleId)
    } catch (error: unknown) {
      console.error('Error deleting role:', error)
      // Revert on error using functional update to avoid stale closure
      setRoles(prev => [...prev, role])
      alert(getErrorMessage(error, 'Erro ao excluir perfil. Tente novamente.'))
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-3" aria-busy="true">
        <span className="sr-only">Carregando permissões...</span>
        <Skeleton className="h-8 w-72" />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    )
  }

  const canEditCell = (role: Role) => !role.isSystem && canManagePermissions

  return (
    <div className={cn('flex min-w-0 flex-col gap-4', className)}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-2">
            <ShieldCheck className="size-5 shrink-0 text-muted-foreground" />
            <h2 className="text-lg font-semibold">Gerenciar Perfis e Permissões</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Configure quais ações cada perfil pode realizar no sistema
          </p>
        </div>
        {canManagePermissions && (
          <Button onClick={handleCreateRole}>
            <Plus className="size-4" /> Novo Perfil
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
        {roles.map((role) => (
          <Card key={role.id} size="sm">
            <CardContent className="flex min-w-0 flex-col gap-1">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <UserIcon className="size-4" aria-hidden="true" />
                  {role.isSystem && <Lock className="size-3.5" aria-label="Perfil do sistema" />}
                </div>
                {!role.isSystem && (
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Editar perfil ${role.name}`}
                      onClick={() => { setSelectedRole(role); setShowEditModal(true) }}
                    >
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Excluir perfil ${role.name}`}
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => handleDeleteRole(role.id)}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                )}
              </div>
              <h3 className="break-words text-sm font-medium">{role.name}</h3>
              <p className="line-clamp-2 text-xs text-muted-foreground">{role.description}</p>
              <div className="mt-2 flex flex-wrap items-center justify-between gap-x-2 text-xs">
                <span className="text-muted-foreground">{role.userCount} usuários</span>
                <span className="text-primary">{role.permissions.length} permissões</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="min-w-0 gap-0 py-0">
        <CardHeader className="border-b py-4">
          <CardTitle className="flex items-center gap-2">
            <Settings className="size-4 text-muted-foreground" aria-hidden="true" />
            <h3>Matrix de Permissões</h3>
          </CardTitle>
          <CardDescription>Configure as permissões específicas para cada perfil</CardDescription>
        </CardHeader>

        <div className="min-w-0 overflow-x-auto">
          <Table className="min-w-[800px]">
            <TableHeader>
              <TableRow>
                <TableHead className="px-4 text-xs uppercase tracking-wider text-muted-foreground">
                  Permissão
                </TableHead>
                {roles.map((role) => (
                  <TableHead key={role.id} className="px-4 text-center text-xs uppercase tracking-wider text-muted-foreground">
                    <div className="flex flex-col items-center">
                      <span className="max-w-20 truncate">{role.name}</span>
                      {role.isSystem && <Lock className="mt-1 size-3" aria-hidden="true" />}
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {permissionGroups.map((group) => (
                <React.Fragment key={group.resource}>
                  <TableRow className="bg-muted/50 hover:bg-muted/50">
                    <TableCell colSpan={roles.length + 1} className="px-4 text-sm font-semibold">
                      {resourceLabels[group.resource as keyof typeof resourceLabels]}
                    </TableCell>
                  </TableRow>
                  {group.permissions.map((permission) => (
                    <TableRow key={permission.id}>
                      <TableCell className="px-4 py-3">
                        <div className="text-sm font-medium">{permission.name}</div>
                        <div className="text-sm text-muted-foreground">{permission.description}</div>
                      </TableCell>
                      {roles.map((role) => {
                        const granted = role.permissions.includes(permission.id)
                        return (
                          <TableCell key={`${role.id}-${permission.id}`} className="px-4 py-3 text-center">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon-sm"
                              onClick={() => handlePermissionToggle(role.id, permission.id)}
                              disabled={!canEditCell(role)}
                              aria-pressed={granted}
                              aria-label={`${permission.name} — ${role.name}`}
                              className={cn(
                                granted ? 'bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary' : 'text-muted-foreground hover:text-primary'
                              )}
                            >
                              {granted ? <Check className="size-4" /> : <X className="size-4" />}
                            </Button>
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={showEditModal && !!selectedRole} onOpenChange={(open) => { if (!open) setShowEditModal(false) }}>
        {selectedRole && (
          <DialogContent fullScreenOnMobile aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle>Editar Perfil: {selectedRole.name}</DialogTitle>
            </DialogHeader>

            <div className="flex min-w-0 flex-col gap-4">
              <FormField label="Nome" htmlFor="role-edit-name">
                <Input
                  id="role-edit-name"
                  type="text"
                  value={selectedRole.name}
                  onChange={(e) => setSelectedRole({ ...selectedRole, name: e.target.value })}
                />
              </FormField>
              <FormField label="Descrição" htmlFor="role-edit-description">
                <Textarea
                  id="role-edit-description"
                  value={selectedRole.description}
                  onChange={(e) => setSelectedRole({ ...selectedRole, description: e.target.value })}
                  rows={3}
                />
              </FormField>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setRoles(roles.map(role => role.id === selectedRole.id ? selectedRole : role))
                  setShowEditModal(false)
                }}
              >
                Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>

      <div className="rounded-lg border border-border bg-muted/40 p-4 text-sm">
        <h4 className="mb-2 flex items-center gap-2 font-medium">
          <Lock className="size-4 text-muted-foreground" aria-hidden="true" />
          Informações Importantes
        </h4>
        <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
          <li>Perfis do sistema (com cadeado) não podem ser editados ou excluídos</li>
          <li>Permissões customizadas só podem ser alteradas em perfis personalizados</li>
          {!canManagePermissions && (
            <li><strong className="text-foreground">Apenas Super Administradores podem modificar permissões do sistema</strong></li>
          )}
          <li>Alterações são aplicadas imediatamente após salvar</li>
          <li>Usuários precisam fazer login novamente para aplicar novas permissões</li>
        </ul>
      </div>
    </div>
  )
}
